import { AppSidebar } from "@/components/navigation/AppSidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useMyFirmList } from "@/hooks/useMyFirmList";
import { useUser } from "@/hooks/useUser";
import { selectActiveFirm } from "@/store/appSelectors";
import { appActions } from "@/store/appSlice";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { AppBreadcrumbs } from "./AppBreadcrumbs";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/constants/routeConstants";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon, SettingsIcon } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NavNotifications } from "@/components/navigation/NavNotifications";
import { NavQuickActions } from "@/components/navigation/NavQuickActions";

// Component for the back button
const BackButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Only show back button if we're not at the root path
  const canGoBack = location.key !== "default";

  if (!canGoBack) return null;

  return (
    <Button variant="ghost" size="icon" onClick={() => navigate(-1)} title={t("common.back")}>
      <ArrowLeftIcon className="h-4 w-4" />
    </Button>
  );
};

export const AppLayout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isError: isUserError, isLoading } = useUser();
  const { firmList } = useMyFirmList();
  const location = useLocation();
  const isTemplateEditorPage = location.pathname === ROUTES.TEMPLATE_EDITOR;
  const isMobile = useIsMobile();

  /**
   * Check if the active firm is set, if not set the first firm in the list
   */
  const activeFirm = useSelector(selectActiveFirm);

  useEffect(() => {
    if (firmList.length > 0 && !activeFirm) {
      dispatch(appActions.setActiveFirm(firmList[0]));
    }
  }, [firmList, activeFirm, dispatch]);

  const siteSettingsComp = useMemo(() => {
    if (!isMobile) {
      return (
        <div className="absolute right-4 top-4 flex gap-2">
          <NavNotifications />
          <ThemeToggle />
          <LanguageSelector />
          <NavQuickActions />
        </div>
      );
    }

    return (
      <>
        <div className="absolute right-4 flex gap-2">
          <NavNotifications />
          <NavQuickActions />
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="flex gap-2">
                <SettingsIcon className="w-2 h-2" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("siteSettings.title")}</DialogTitle>
                <DialogDescription>
                  <div className="flex flex-row gap-4 items-center mt-4">
                    <div className="flex w-[80px]">{t("siteSettings.theme")}</div>
                    <ThemeToggle />
                  </div>
                  <div className="flex flex-row gap-4 items-center mt-4">
                    <div className="flex w-[80px]">{t("siteSettings.language")}</div>
                    <LanguageSelector />
                  </div>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
      </>
    );
  }, [isMobile, t]);

  if (isUserError) {
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return (
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="flex justify-center">
          <LoadingSmall />
        </div>
        <div className="text-sm text-muted-foreground text-center">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 border-b mb-4">
          <div className="flex items-center gap-2 px-4">
            {isMobile && <SidebarTrigger className="-ml-1" />}
            {/* <Separator orientation="vertical" className="mr-2 h-4" /> */}
            <BackButton />
            <AppBreadcrumbs />
            {siteSettingsComp}
          </div>
        </header>
        {/* If user is in template editor page, remove the padding to provide better UX */}
        <div className={cn("flex flex-1 flex-col gap-4", isTemplateEditorPage ? "p-0" : "p-4 pt-0")}>
          <ErrorBoundary
            fallback={
              <Alert className="mt-2" variant="destructive">
                <AlertTitle>{t("common.error")}</AlertTitle>
                <AlertDescription>{t("Unexpected error occurred, please try again later")}</AlertDescription>
              </Alert>
            }
          >
            <Outlet />
          </ErrorBoundary>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
